import { HSpacer, IconButton, Text, TopAppBar, VSpacer } from '@/components/DesignSystem';
import { Routes } from '@/constants/Routes';
import { useDashboard } from '@/hooks/useDashboard';
import {
  NewProgramTierBenefitsBanner,
} from '@/pages/CustomerDashboard/Benefits/NewProgramTierBenefitsBanner';
import { TierBenefitsModal } from '@/pages/CustomerDashboard/Benefits/TierBenefitsModal';
import { defaultDashboardData } from '@/pages/CustomerDashboard/CustomerDashboard';
import { TierImage } from '@/pages/CustomerDashboard/TierImage';
import { ResolvedTier } from '@api/data/RewardsProgramData';
import { ApiCategory, ApiManufacturer, ApiPromotion, ApiRetailer } from '@api/interfaces';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import FiberManualRecord from '@mui/icons-material/FiberManualRecord';
import ArrowBack from '@mui/icons-material/ArrowBack';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { ButtonBase, CircularProgress, Container, Divider, Stack, useTheme } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { getProgramInfo, useDashboardNavigate } from './helpers';

export const BenefitsPage = () => {
  const { t } = useTranslation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('accessToken') ?? params.accessToken;
  const navigateToPage = useDashboardNavigate();

  const { data: dashboardData } = useDashboard(accessToken);

  const {
    activeFarmerRewards,
    activeProgram,
    categories,
    manufacturers,
    promotions,
    retailer,
  } = dashboardData ?? defaultDashboardData;
  const brandColor = dashboardData?.retailer.brandColor ?? '#97743a';

  const {
    hasTierGraphics,
  } = useMemo(() => (
    getProgramInfo(activeProgram, activeFarmerRewards, !dashboardData)
  ), [activeProgram, activeFarmerRewards, dashboardData]);

  if (!dashboardData) {
    return (
      <Container>
        <Stack alignItems="center">
          <VSpacer size="14" />
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  if (!activeProgram || !activeFarmerRewards || !categories) {
    return null;
  }

  const Header = (
    <TopAppBar
      isStatic
      leftAccessory={(
        <IconButton
          onClick={() => navigateToPage(Routes.CUSTOMER_DASHBOARD, accessToken)}
          sx={{ color: 'white' }}
          testID="back-button"
        >
          <ArrowBack />
        </IconButton>
      )}
      rightAccessory={<HSpacer size="7" />}
      sx={{ bgcolor: brandColor }}
      testID="top-app-bar"
    >
      <Text category="title-large" sx={{ color: 'white' }}>
        {t($ => $.customerDashboard.benefits.title)}
      </Text>
    </TopAppBar>
  );

  return  (
    <>
      <Stack
        bgcolor="#F7F7F7"
        maxWidth="599px"
        minHeight="100vh"
        minWidth="360px"
      >
        {Header}
        <NewProgramTierBenefitsBanner program={activeProgram} />
        <Stack divider={<Divider />}>
          {activeProgram.tiers.map((tier) => (
            <TierBenefitsCard
              brandColor={brandColor}
              categories={categories}
              hasTierGraphics={hasTierGraphics}
              key={tier.id}
              manufacturers={manufacturers}
              numCategoriesToHurdle={tier.minimumSegments}
              program={activeProgram}
              promotions={promotions}
              retailer={retailer}
              spendToHurdle={tier.minimumDollars}
              testID={`tier-${tier.rank}-benefits-card`}
              tier={tier}
            />
          ))}
        </Stack>
        <Divider />
      </Stack>
    </>
  );
};

interface TierBenefitsCardProps {
  brandColor: string,
  categories: ApiCategory[],
  hasTierGraphics: boolean,
  manufacturers: ApiManufacturer[],
  numCategoriesToHurdle: number,
  program: ApiRewardsProgram,
  promotions: ApiPromotion[],
  retailer: ApiRetailer,
  spendToHurdle: number,
  testID: string,
  tier: ResolvedTier,
}

const TierBenefitsCard = ({
  brandColor,
  categories,
  hasTierGraphics,
  manufacturers,
  numCategoriesToHurdle,
  program,
  promotions,
  retailer,
  spendToHurdle,
  testID,
  tier,
}: TierBenefitsCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [showTierBenefitsModal, setShowTierBenefitsModal] = useState(false);

  const categoryHurdleText = numCategoriesToHurdle > 0
    ? t($ => $.customerDashboard.benefits.hurdle, { count: numCategoriesToHurdle })
    : null;
  const spendHurdleText = spendToHurdle > 0
    ? t($ => $.customerDashboard.benefits.spend, { amount: spendToHurdle })
    : null;

  const BulletPoint = (
    <Stack alignItems="center" justifyContent="center" minHeight="20px" minWidth="20px">
      <FiberManualRecord sx={{ height: '6px', width: '6px' }}/>
    </Stack>
  );

  return (
    <ButtonBase onClick={() => setShowTierBenefitsModal(!showTierBenefitsModal)}>
      <Stack bgcolor={theme.palette.background.default} width="100%">
        <Stack alignItems="center" direction="row" justifyContent="space-between" p="16px">
          <Stack direction="row">
            {hasTierGraphics && (
              <>
                <TierImage hasTierGraphics={hasTierGraphics} tier={tier} />
                <HSpacer size="3"/>
              </>
            )}
            <Stack>
              <Stack alignItems="center" direction="row">
                <Text fontSize="20px" fontWeight={500}>
                  {tier.name} {t($ => $.customerDashboard.benefits.tier)}
                </Text>
              </Stack>
              <VSpacer size="1" />
              {spendHurdleText && (
                <Stack direction="row">
                  {BulletPoint}
                  <Text category="body-medium">
                    {spendHurdleText}
                  </Text>
                </Stack>
              )}
              {categoryHurdleText && (
                <Stack direction="row">
                  {BulletPoint}
                  <Text category="body-medium">
                    {categoryHurdleText}
                  </Text>
                </Stack>
              )}
            </Stack>
          </Stack>
          <HSpacer size="3" />
          <IconButton
            onClick={() => setShowTierBenefitsModal(!showTierBenefitsModal)}
            sx={{ color: theme.palette.grey[400] }}
            testID={`${testID}-expand-button`}
            variant="outlined"
          >
            <KeyboardArrowDown sx={{ color: 'black', height: '24px', width: '24px' }}/>
          </IconButton>
        </Stack>
      </Stack>
      {showTierBenefitsModal && (
        <TierBenefitsModal
          brandColor={brandColor}
          categories={categories}
          hasTierGraphics={hasTierGraphics}
          manufacturers={manufacturers}
          onClose={() => setShowTierBenefitsModal(false)}
          program={program}
          promotions={promotions}
          retailer={retailer}
          tier={tier}
        />
      )}
    </ButtonBase>
  );
};

